/** Browser-first Code Editor language capability matrix (Monaco language ids). */

export type LanguageCapabilitySource =
  | 'monaco-builtin'
  | 'browser-parser'
  | 'highlight-only'
  | 'server-lsp';

export type LanguageCapabilityTier = 'rich' | 'parse-format' | 'highlight' | 'lsp-planned';

export type LanguageCapabilities = {
  formatter: boolean;
  validator: boolean;
  linter: boolean;
  completion: boolean;
  hover: boolean;
  symbols: boolean;
  rename: boolean;
  quickFix: boolean;
  semanticTokens: boolean;
};

export type LanguageCommentSyntax = {
  line?: string;
  block?: { start: string; end: string };
};

export type LanguageCapability = {
  monacoLanguageId: string;
  displayName: string;
  extensions: readonly string[];
  filenames: readonly string[];
  indentation: { tabSize: number; insertSpaces: boolean };
  comments?: LanguageCommentSyntax;
  capabilities: LanguageCapabilities;
  source: LanguageCapabilitySource;
  tier: LanguageCapabilityTier;
};

const none: LanguageCapabilities = {
  formatter: false,
  validator: false,
  linter: false,
  completion: false,
  hover: false,
  symbols: false,
  rename: false,
  quickFix: false,
  semanticTokens: false
};

const richBuiltin: LanguageCapabilities = {
  formatter: true,
  validator: true,
  linter: true,
  completion: true,
  hover: true,
  symbols: true,
  rename: true,
  quickFix: true,
  semanticTokens: true
};

const parseFormat: LanguageCapabilities = {
  formatter: true,
  validator: true,
  linter: false,
  completion: false,
  hover: false,
  symbols: false,
  rename: false,
  quickFix: false,
  semanticTokens: false
};

const highlightOnly: LanguageCapabilities = {
  ...none
};

function lang(
  partial: Omit<LanguageCapability, 'capabilities'> & {
    capabilities?: Partial<LanguageCapabilities>;
    base?: LanguageCapabilities;
  }
): LanguageCapability {
  const base = partial.base ?? none;
  return {
    monacoLanguageId: partial.monacoLanguageId,
    displayName: partial.displayName,
    extensions: partial.extensions,
    filenames: partial.filenames,
    indentation: partial.indentation,
    comments: partial.comments,
    capabilities: { ...base, ...partial.capabilities },
    source: partial.source,
    tier: partial.tier
  };
}

const REGISTRY: readonly LanguageCapability[] = [
  lang({
    monacoLanguageId: 'javascript',
    displayName: 'JavaScript',
    extensions: ['js', 'mjs', 'cjs', 'jsx'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'typescript',
    displayName: 'TypeScript',
    extensions: ['ts', 'mts', 'cts', 'tsx'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'json',
    displayName: 'JSON',
    extensions: ['json', 'jsonc', 'jdoc', 'jsheet', 'jenc'],
    filenames: ['.prettierrc', '.eslintrc'],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'html',
    displayName: 'HTML',
    extensions: ['html', 'htm'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { block: { start: '<!--', end: '-->' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'css',
    displayName: 'CSS',
    extensions: ['css'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'scss',
    displayName: 'SCSS',
    extensions: ['scss'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'less',
    displayName: 'Less',
    extensions: ['less'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: richBuiltin,
    source: 'monaco-builtin',
    tier: 'rich'
  }),
  lang({
    monacoLanguageId: 'yaml',
    displayName: 'YAML',
    extensions: ['yaml', 'yml'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#' },
    base: parseFormat,
    source: 'browser-parser',
    tier: 'parse-format'
  }),
  lang({
    monacoLanguageId: 'xml',
    displayName: 'XML',
    extensions: ['xml', 'xsd', 'xsl', 'xslt', 'svg', 'drawio', 'dio'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { block: { start: '<!--', end: '-->' } },
    base: parseFormat,
    source: 'browser-parser',
    tier: 'parse-format'
  }),
  lang({
    monacoLanguageId: 'sql',
    displayName: 'SQL',
    extensions: ['sql'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '--', block: { start: '/*', end: '*/' } },
    base: parseFormat,
    capabilities: { validator: false },
    source: 'browser-parser',
    tier: 'parse-format'
  }),
  lang({
    monacoLanguageId: 'csharp',
    displayName: 'C#',
    extensions: ['cs', 'csx'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'python',
    displayName: 'Python',
    extensions: ['py', 'pyw'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '#' },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'powershell',
    displayName: 'PowerShell',
    extensions: ['ps1', 'psm1', 'psd1'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '#', block: { start: '<#', end: '#>' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'shell',
    displayName: 'Shell',
    extensions: ['sh', 'bash', 'zsh'],
    filenames: ['makefile', 'gnumakefile'],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'bat',
    displayName: 'Batch',
    extensions: ['bat', 'cmd'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: 'REM' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'cpp',
    displayName: 'C/C++',
    extensions: ['c', 'cc', 'cpp', 'h', 'hpp'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'go',
    displayName: 'Go',
    extensions: ['go'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: false },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'rust',
    displayName: 'Rust',
    extensions: ['rs'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'java',
    displayName: 'Java',
    extensions: ['java'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'kotlin',
    displayName: 'Kotlin',
    extensions: ['kt', 'kts'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'server-lsp',
    tier: 'lsp-planned'
  }),
  lang({
    monacoLanguageId: 'php',
    displayName: 'PHP',
    extensions: ['php'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'ruby',
    displayName: 'Ruby',
    extensions: ['rb'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'swift',
    displayName: 'Swift',
    extensions: ['swift'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '/*', end: '*/' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'vb',
    displayName: 'Visual Basic',
    extensions: ['vb'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: "'" },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'fsharp',
    displayName: 'F#',
    extensions: ['fs', 'fsx'],
    filenames: [],
    indentation: { tabSize: 4, insertSpaces: true },
    comments: { line: '//', block: { start: '(*', end: '*)' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'graphql',
    displayName: 'GraphQL',
    extensions: ['graphql', 'gql'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#', block: { start: '"""', end: '"""' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'dockerfile',
    displayName: 'Dockerfile',
    extensions: ['dockerfile'],
    filenames: ['dockerfile'],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'markdown',
    displayName: 'Markdown',
    extensions: ['md', 'markdown'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { block: { start: '<!--', end: '-->' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'ini',
    displayName: 'INI / config',
    extensions: ['ini', 'toml', 'properties', 'cfg', 'conf'],
    filenames: ['.editorconfig', '.npmrc', '.gitconfig'],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: ';' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'lua',
    displayName: 'Lua',
    extensions: ['lua'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '--', block: { start: '--[[', end: ']]' } },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'r',
    displayName: 'R',
    extensions: ['r'],
    filenames: [],
    indentation: { tabSize: 2, insertSpaces: true },
    comments: { line: '#' },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  }),
  lang({
    monacoLanguageId: 'plaintext',
    displayName: 'Plain Text',
    extensions: ['txt', 'log', 'env', 'csv', 'tsv', 'gitignore', 'gitattributes'],
    filenames: ['.gitignore', '.gitattributes'],
    indentation: { tabSize: 2, insertSpaces: true },
    base: highlightOnly,
    source: 'highlight-only',
    tier: 'highlight'
  })
];

const BY_ID = new Map(REGISTRY.map((entry) => [entry.monacoLanguageId, entry]));

const EXTENSION_TO_LANGUAGE: Record<string, string> = {};
const FILENAME_TO_LANGUAGE: Record<string, string> = {};

for (const entry of REGISTRY) {
  for (const ext of entry.extensions) {
    EXTENSION_TO_LANGUAGE[ext.toLowerCase()] = entry.monacoLanguageId;
  }
  for (const name of entry.filenames) {
    FILENAME_TO_LANGUAGE[name.toLowerCase()] = entry.monacoLanguageId;
  }
}

const TIER_LABELS: Record<LanguageCapabilityTier, string> = {
  rich: 'Rich built-in IntelliSense',
  'parse-format': 'Parse / format / validate in browser',
  highlight: 'Syntax highlight and safe edit',
  'lsp-planned': 'Highlight now; project LSP planned'
};

const SOURCE_LABELS: Record<LanguageCapabilitySource, string> = {
  'monaco-builtin': 'Monaco built-in',
  'browser-parser': 'Browser parser',
  'highlight-only': 'Highlight only',
  'server-lsp': 'Server LSP (planned)'
};

function basename(pathOrName: string): string {
  const normalized = pathOrName.replace(/\\/g, '/');
  const seg = normalized.split('/').filter(Boolean).pop();
  return seg ?? pathOrName;
}

function extensionOf(pathOrExtension: string): string {
  const trimmed = pathOrExtension.trim().toLowerCase();
  if (!trimmed) return '';
  if (trimmed.startsWith('.') && trimmed.lastIndexOf('.') === 0) {
    return trimmed.slice(1);
  }
  const base = basename(trimmed);
  const lastDot = base.lastIndexOf('.');
  if (lastDot < 0 || lastDot === base.length - 1) return '';
  return base.slice(lastDot + 1);
}

export function getLanguageCapability(languageId: string): LanguageCapability {
  return BY_ID.get(languageId) ?? BY_ID.get('plaintext')!;
}

export function listLanguageCapabilities(): readonly LanguageCapability[] {
  return REGISTRY;
}

/** Filename-aware language resolution (Dockerfile, Makefile, .gitignore, …) then extension. */
export function resolveLanguageFromPath(path: string): string {
  const base = basename(path).toLowerCase();
  if (base && FILENAME_TO_LANGUAGE[base]) {
    return FILENAME_TO_LANGUAGE[base];
  }

  const trimmed = path.trim().toLowerCase();
  if (trimmed.startsWith('.') && trimmed.lastIndexOf('.') === 0) {
    const asFilename = FILENAME_TO_LANGUAGE[trimmed];
    if (asFilename) return asFilename;
    const asExt = EXTENSION_TO_LANGUAGE[trimmed.slice(1)];
    if (asExt) return asExt;
  }

  const ext = extensionOf(path);
  if (ext && EXTENSION_TO_LANGUAGE[ext]) {
    return EXTENSION_TO_LANGUAGE[ext];
  }

  return 'plaintext';
}

export function describeSupport(languageId: string): string {
  const entry = getLanguageCapability(languageId);
  const caps = Object.entries(entry.capabilities)
    .filter(([, enabled]) => enabled)
    .map(([name]) => name);
  const capText = caps.length > 0 ? caps.join(', ') : 'editing only';
  return `${entry.displayName} — ${TIER_LABELS[entry.tier]} (${SOURCE_LABELS[entry.source]}; ${capText})`;
}
